// Your routes — and the navigation binding's, which is the part worth knowing.
//
// THIS FILE IS REPLACED when the navigation shell is re-applied. `bottom-tabs`,
// `stack` and `sidebar` produce different routers, so the binding owns it and
// `appfactory spec apply` rewrites it. Edit it freely, and commit before
// running `spec apply`.

import { computed } from 'vue';
import { createRouter, createMemoryHistory } from '@ionic/vue-router';
// `icon` is an IMPORTED ionicon, not a name: `<ion-icon name="…">` fetches its
// SVG from a CDN the content-security-policy blocks and an offline app cannot
// reach, which leaves a blank tab bar on a plane.
import {
  ticketOutline, receiptOutline, layersOutline, footballOutline, statsChartOutline,
  flagOutline,
} from 'ionicons/icons';

import TabsShell from './pages/TabsShell.vue';
import { flags } from './lib/flags.js';

// The screens themselves come from ./product-routes.js.
import { PRODUCT_ROUTES, DESTINATION_ROUTES, HOME_ROUTE } from './product-routes.js';

/** Where the app opens. main.js pushes this. */
export const HOME = HOME_ROUTE;

// Today is the pad; Matches is the board the pad is printed from; Racing is the
// same afternoon read as a card; History is the stack of carbon copies;
// Insights is what the stack adds up to. Each icon is the THING the tab is — a
// ticket, a ball, a winning post, a receipt, a chart — and all five are outlines
// at the same weight, which is what makes the row read as one set rather than
// five decisions.
//
// `flagOutline` is the racing one: the flag that drops is the start of a race
// and the chequered one is the end of it, and neither a horse nor a stopwatch
// is available at this weight. `layersOutline` stays here for Strategies even
// though Strategies is no longer a tab — the map is keyed by route id, and a
// screen that comes back to the bar should not have to find its icon again.
const TAB_ICONS = {
  today: ticketOutline,
  matches: footballOutline,
  racing: flagOutline,
  strategies: layersOutline,
  history: receiptOutline,
  insights: statsChartOutline,
};

// Which destinations a tab is allowed to exist for. Absent means always.
const TAB_FLAGS = { racing: 'RACING', strategies: 'STRATEGIES', insights: 'INSIGHTS' };

/**
 * What the tab bar shows. A COMPUTED, not an array: a destination behind a
 * feature flag has to appear the moment Labs is switched on, and the shell
 * reads this through `unref` either way, so TabsShell needs no change.
 */
export const TABS = computed(() => DESTINATION_ROUTES
  .filter((r) => !TAB_FLAGS[r.id] || flags[TAB_FLAGS[r.id]])
  .map((r) => ({
    name: r.id,
    label: r.label,
    path: r.path,
    icon: TAB_ICONS[r.id] ?? ticketOutline,
  })));

export default createRouter({
  history: createMemoryHistory(),
  routes: [
    {
      // The tab shell owns its own outlet, so each tab keeps its own
      // navigation stack: opening a ticket leaves the tab bar in place and the
      // back button working.
      path: '/tabs/',
      component: TabsShell,
      children: [
        { path: '', redirect: HOME },
        // `props: true` on every route, so a screen with `:id` in its path gets
        // it as a prop as well as through useRoute().
        ...PRODUCT_ROUTES.map((r) => ({
          path: r.path.replace(/^\/tabs\//, ''),
          name: r.id,
          component: r.component,
          props: true,
        })),
      ],
    },
  ],
});
